import {
  type ActionBucket,
  type ActionPlan,
  type ActionPlanStatus,
  type ActionPlanStep,
  type SignalAdminDraft,
  type SignalAdminRegistry,
} from "./types";

export const BUCKET_ORDER: ActionBucket[] = [
  "single_independent",
  "single_dependent",
  "multiple_independent",
  "multiple_dependent",
];

export const MAX_STEPS = 6;

/** How many steps the card's own user takes. */
export const usersActions = (steps: Pick<ActionPlanStep, "owner">[]) =>
  steps.filter(s => s.owner === "you").length;

/** Same rule as the backend's bucket_of: single/multiple counts the card
 * user's own actions; dependent = someone else also has a step. */
export const bucketOf = (steps: Pick<ActionPlanStep, "owner">[]) =>
  `${usersActions(steps) > 1 ? "multiple" : "single"}_${
    steps.every(s => s.owner === "you") ? "independent" : "dependent"
  }` as ActionBucket;

/** The user's line on an awareness plan's card. */
export const USER_AWARENESS_NOTE =
  "This card clears when the underlying data changes, not when you act.";

/** What a plan puts on the card — mirrors the backend's _plan_cta. Single ·
 * independent: the button is the action and its detail sits above it.
 * Single · dependent: the button is the user's own action. Multiple: the
 * button opens the steps. */
export const planCardCta = (
  steps: Pick<ActionPlanStep, "owner" | "title" | "detail">[]
): { label: string; detail: string | null; ownIndex: number } => {
  const bucket = bucketOf(steps);
  const ownIndex = Math.max(
    steps.findIndex(s => s.owner === "you"),
    0
  );
  if (bucket === "single_independent") {
    return { label: steps[0].title, detail: steps[0].detail || null, ownIndex };
  }
  if (bucket === "single_dependent") {
    return { label: steps[ownIndex].title, detail: null, ownIndex };
  }
  return { label: `See the ${steps.length} steps`, detail: null, ownIndex };
};

export interface DraftPlan {
  steps: ActionPlanStep[];
  rationale: string;
  status: ActionPlanStatus;
  note: string;
  awareness: boolean;
  bucket: ActionBucket;
  /** The draft holds a change the applied config doesn't. */
  staged: boolean;
}

const sameSteps = (a: ActionPlanStep[], b: ActionPlanStep[]) =>
  JSON.stringify(a) === JSON.stringify(b);

/** What the admin sees for a signal: their staged plan, else the applied
 * one, else ExecBridge's proposal. */
export const draftPlan = (
  registry: SignalAdminRegistry,
  draft: SignalAdminDraft,
  applied: Record<string, ActionPlan>,
  definitionId: string
): DraftPlan | null => {
  const stored = draft.action_plans?.[definitionId];
  const staged =
    JSON.stringify(stored ?? null) !==
    JSON.stringify(applied[definitionId] ?? null);
  if (stored) {
    return {
      ...stored,
      awareness: stored.awareness === true,
      bucket: bucketOf(stored.steps),
      staged,
    };
  }
  const proposal = registry.action_plan_defaults?.[definitionId];
  if (!proposal) return null;
  return {
    steps: proposal.steps,
    rationale: proposal.rationale,
    status: "proposed",
    note: "",
    awareness: proposal.awareness === true,
    bucket: bucketOf(proposal.steps),
    staged,
  };
};

export const isEditedFromProposal = (
  registry: SignalAdminRegistry,
  definitionId: string,
  steps: ActionPlanStep[]
) => {
  const proposal = registry.action_plan_defaults?.[definitionId];
  return !proposal || !sameSteps(proposal.steps, steps);
};

/** Mirrors the backend's _action_plan_errors for what the editor can get
 * wrong. */
export const planErrors = (plan: ActionPlan): string[] => {
  const errors: string[] = [];
  if (plan.steps.length < 1 || plan.steps.length > MAX_STEPS) {
    errors.push(`A plan needs 1 to ${MAX_STEPS} sub-actions.`);
  }
  if (plan.steps.length && usersActions(plan.steps) === 0) {
    errors.push(
      "Give the card's user at least one action — otherwise there's no reason to show them the card."
    );
  }
  plan.steps.forEach((step, i) => {
    if (step.title.trim().length < 3) {
      errors.push(`Sub-action ${i + 1} needs a title.`);
    }
  });
  if (
    plan.steps.length &&
    bucketOf(plan.steps) === "single_independent" &&
    !plan.steps[0].target
  ) {
    errors.push(
      "A single-independent action is a one-page CTA, so it needs a target page."
    );
  }
  if (plan.awareness && plan.steps.length !== 1) {
    errors.push(
      "An awareness signal is one action (review) — remove the extra sub-actions or clear the flag."
    );
  }
  if (plan.status === "changes_requested" && !plan.note.trim()) {
    errors.push("Say what needs to change.");
  }
  return errors;
};

export const STATUS_META: Record<
  ActionPlanStatus,
  { label: string; className: string }
> = {
  proposed: {
    label: "Proposed",
    className: "bg-amber-50 text-amber-800 ring-amber-600/20",
  },
  approved: {
    label: "Approved",
    className: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  },
  changes_requested: {
    label: "Changes requested",
    className: "bg-red-50 text-red-700 ring-red-600/20",
  },
};
